import { getAccessToken } from '../authentication/supabaseService';

// Valid notification types (matches backend)
export const NOTIFICATION_TYPES = ['task_reminder', 'change_alert', 'announcement', 'leadership'] as const;
export type NotificationType = typeof NOTIFICATION_TYPES[number];

export interface Notification {
  id: string;
  restaurantId: string;
  title: string;
  description: string;
  notificationType: string;
  assignedTo: string[];
  dueAt: Date | null;
  createdAt: Date;
  createdBy: string | null;
}

export interface NotificationCreateRequest {
  restaurantId: string;
  title: string;
  description: string;
  notificationType: string;
  assignedTo?: string[];
  dueAt?: string | null;
}

export interface NotificationUpdateRequest {
  id: string;
  title?: string;
  description?: string;
  notificationType?: string;
  assignedTo?: string[];
  dueAt?: string | null;
}

// Helper to parse notification dates
function parseNotification(item: any): Notification {
  return {
    ...item,
    dueAt: item.dueAt ? new Date(item.dueAt) : null,
    createdAt: new Date(item.createdAt),
  };
}

// Get all notifications for a restaurant (admin view - no filtering)
export async function fetchNotificationsByRestaurant(
  restaurantId: string
): Promise<Notification[]> {
  const token = await getAccessToken();
  const res = await fetch(`/api/notifications/restaurant/${restaurantId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch notifications: ${res.status} ${res.statusText}`);
  }
  const data = await res.json();
  return data.map(parseNotification);
}

// Get notifications for current user (filtered by assigned_to)
export async function fetchNotificationsForUser(
  restaurantId: string
): Promise<Notification[]> {
  const token = await getAccessToken();
  const res = await fetch(`/api/notifications/restaurant/${restaurantId}/user`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch notifications: ${res.status} ${res.statusText}`);
  }
  const data = await res.json();
  return data.map(parseNotification);
}

// Get a specific notification by ID
export async function fetchNotificationById(id: string): Promise<Notification> {
  const token = await getAccessToken();
  const res = await fetch(`/api/notifications/${id}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch notification: ${res.status} ${res.statusText}`);
  }
  const data = await res.json();
  return parseNotification(data);
}

// Create a new notification
export async function createNotification(
  request: NotificationCreateRequest
): Promise<Notification> {
  const token = await getAccessToken();
  const res = await fetch('/api/notifications/create', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(request),
  });
  if (!res.ok) {
    throw new Error(`Failed to create notification: ${res.status} ${res.statusText}`);
  }
  const data = await res.json();
  return parseNotification(data);
}

// Update a notification
export async function updateNotification(
  request: NotificationUpdateRequest
): Promise<Notification> {
  const token = await getAccessToken();
  const res = await fetch('/api/notifications/update', {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(request),
  });
  if (!res.ok) {
    throw new Error(`Failed to update notification: ${res.status} ${res.statusText}`);
  }
  const data = await res.json();
  return parseNotification(data);
}

// Delete a notification
export async function deleteNotification(id: string): Promise<void> {
  const token = await getAccessToken();
  const res = await fetch(`/api/notifications/${id}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    throw new Error(`Failed to delete notification: ${res.status} ${res.statusText}`);
  }
}
